import { useEffect, useRef, useState } from 'react';

import { getCrawlResults, getChannelCrawlResults } from 'api/line';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import { commonStrings, lineStrings } from 'constants/strings';
import { useCrawlMigration, useMigrateFromCsv, useLineChannelAccounts, useChannelCrawl, useBulkRegisterChannels } from 'hooks';
import type { UseSendMigrationMessageResult } from 'hooks/line/useSendMigrationMessage';
import type { TMigrateFromCsvEntry, TBulkRegisterChannelEntry } from 'types';
import { parseMigrationCsv } from './migrationCsv';
import { parseChannelCrawlCsv } from './channelCrawlCsv';

type MigrationModalProps = {
  open: boolean;
  onClose: () => void;
  messageSend: UseSendMigrationMessageResult;
  organizationId: number;
};

function ProgressBar({ current, total, label }: { current: number; total: number; label: string }) {
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between text-xs text-muted">
        <span className="font-medium">{label}</span>
        <span>{current}/{total}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-border/40" role="progressbar" aria-valuenow={current} aria-valuemin={0} aria-valuemax={total}>
        <div className="h-full rounded-full bg-accent transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-muted/70">{children}</p>
  );
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

function triggerCsvDownload(filename: string, header: string, rows: string[]) {
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type CsvMode = 'channel' | 'friend';

export function MigrationModal({ open, onClose, messageSend, organizationId }: MigrationModalProps) {
  const crawlStrings = lineStrings.management.migration.crawl;
  const csvStrings = crawlStrings.csvTab;
  const msgStrings = lineStrings.management.migration.migrationMessage;

  const [url, setUrl] = useState('');
  const [loginId, setLoginId] = useState('');
  const [loginPass, setLoginPass] = useState('');

  const [channelDownloadError, setChannelDownloadError] = useState<string | null>(null);
  const { startCrawl: startChannelCrawlFn, isRunning: isChannelCrawlRunning, progress: channelCrawlProgress, reset: resetChannelCrawl } = useChannelCrawl();

  const [friendDownloadError, setFriendDownloadError] = useState<string | null>(null);
  const { startCrawl, isRunning: isFriendCrawlRunning, progress: friendCrawlProgress, reset: resetFriendCrawl } = useCrawlMigration();

  const [csvMode, setCsvMode] = useState<CsvMode>('channel');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvEntries, setCsvEntries] = useState<TMigrateFromCsvEntry[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const { start: startMigrate, isRunning: isMigrateRunning, progress: migrateProgress, reset: resetMigrate } = useMigrateFromCsv();

  const channelFileInputRef = useRef<HTMLInputElement>(null);
  const [channelCsvEntries, setChannelCsvEntries] = useState<TBulkRegisterChannelEntry[]>([]);
  const [channelConfirmed, setChannelConfirmed] = useState(false);
  const { start: startRegister, isRunning: isRegisterRunning, progress: registerProgress, reset: resetRegister } = useBulkRegisterChannels();

  const { accounts } = useLineChannelAccounts(organizationId);
  const { send: sendMessages, isRunning: isMessageRunning, progress: messageProgress, reset: resetMessage } = messageSend;

  useEffect(() => {
    if (open) {
      setUrl('');
      setLoginId('');
      setLoginPass('');
      setChannelDownloadError(null);
      setFriendDownloadError(null);
      setCsvMode('channel');
      setCsvEntries([]);
      setConfirmed(false);
      setChannelCsvEntries([]);
      setChannelConfirmed(false);
      resetChannelCrawl();
      resetFriendCrawl();
      resetMigrate();
      resetMessage();
      resetRegister();
    }
  }, [open, resetChannelCrawl, resetFriendCrawl, resetMigrate, resetMessage, resetRegister]);

  const isAnyRunning = isChannelCrawlRunning || isFriendCrawlRunning || isMigrateRunning || isRegisterRunning || isMessageRunning;

  const handleClose = () => {
    if (isAnyRunning) return;
    resetChannelCrawl();
    resetFriendCrawl();
    resetMigrate();
    resetMessage();
    resetRegister();
    onClose();
  };

  const canStartCrawl = !!url && !!loginId && !!loginPass && !isAnyRunning;

  const handleStartCrawlAll = async () => {
    if (!canStartCrawl) return;
    await startChannelCrawlFn(url, loginId, loginPass, organizationId);
    await startCrawl(url, loginId, loginPass, organizationId);
  };

  const handleDownloadChannelCsv = async () => {
    if (!channelCrawlProgress.jobId) return;
    setChannelDownloadError(null);
    try {
      const results = await getChannelCrawlResults(channelCrawlProgress.jobId, organizationId);
      triggerCsvDownload(
        `channels_${channelCrawlProgress.jobId}.csv`,
        'basic_id,channel_id,channel_secret',
        results.map((r) => [r.basic_id, r.channel_id, r.channel_secret].map(escapeCsvField).join(',')),
      );
    } catch {
      setChannelDownloadError(csvStrings.downloadFailed);
    }
  };

  const handleDownloadFriendCsv = async () => {
    if (!friendCrawlProgress.jobId) return;
    setFriendDownloadError(null);
    try {
      const results = await getCrawlResults(friendCrawlProgress.jobId, organizationId);
      triggerCsvDownload(
        `friends_${friendCrawlProgress.jobId}.csv`,
        'member_id,line_user_id,basic_id',
        results.map((r) => [r.member_id, r.line_user_id, r.basic_id].map(escapeCsvField).join(',')),
      );
    } catch {
      setFriendDownloadError(csvStrings.downloadFailed);
    }
  };

  const handleFriendFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      setCsvEntries(parseMigrationCsv(reader.result as string));
      setConfirmed(false);
    };
    reader.readAsText(file);
  };

  const handleChannelFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      setChannelCsvEntries(parseChannelCrawlCsv(reader.result as string));
      setChannelConfirmed(false);
    };
    reader.readAsText(file);
  };

  const handleConfirmMigrate = () => {
    setConfirmed(true);
    startMigrate(csvEntries, organizationId);
  };

  const handleConfirmRegister = () => {
    setChannelConfirmed(true);
    startRegister(channelCsvEntries, organizationId);
  };

  const handleSendMessages = () => {
    const channelIds = accounts.map((a) => a.id);
    if (channelIds.length === 0) return;
    sendMessages(channelIds, organizationId);
  };

  const chPhase = channelCrawlProgress.phase;
  const frPhase = friendCrawlProgress.phase;
  const isChDone = chPhase === 'confirm' || chPhase === 'completed';
  const isFrDone = frPhase === 'confirm' || frPhase === 'completed';
  const isChCrawling = chPhase === 'starting' || chPhase === 'crawling';
  const isFrCrawling = frPhase === 'starting' || frPhase === 'crawling';
  const hasCrawlResults = isChDone || isFrDone;
  const isCrawling = isChCrawling || isFrCrawling;

  const migratePhase = migrateProgress.phase;
  const registerPhase = registerProgress.phase;

  return (
    <Modal open={open} onClose={handleClose} size="lg">
      <div className="grid gap-5">
        {/* Guide */}
        <div className="grid gap-1 rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-800">
          <p>{csvStrings.migrationGuide1}</p>
          <p>{csvStrings.migrationGuide2}</p>
        </div>

        {/* Section 1: Data Fetch */}
        <div className="grid gap-4">
          <SectionLabel>{crawlStrings.crawlTitle}</SectionLabel>

          <div className="rounded-2xl border border-border/70 bg-surface/70 p-4">
            <div className="grid gap-3">
              <Input
                id="crawl-url"
                aria-label={crawlStrings.urlLabel}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={crawlStrings.urlPlaceholder}
                disabled={isAnyRunning}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  id="crawl-login-id"
                  aria-label={crawlStrings.loginIdLabel}
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder={crawlStrings.loginIdLabel}
                  disabled={isAnyRunning}
                />
                <Input
                  id="crawl-login-pass"
                  aria-label={crawlStrings.loginPassLabel}
                  type="password"
                  value={loginPass}
                  onChange={(e) => setLoginPass(e.target.value)}
                  placeholder={crawlStrings.loginPassLabel}
                  disabled={isAnyRunning}
                />
              </div>
              <Button
                type="button"
                size="sm"
                variant="primary"
                fullWidth
                disabled={!canStartCrawl}
                loading={isCrawling}
                onClick={handleStartCrawlAll}
              >
                {crawlStrings.startButton}
              </Button>
            </div>
          </div>

          {/* Crawl progress */}
          {isCrawling && (
            <div className="grid gap-3 rounded-2xl border border-border/70 bg-surface/70 p-4">
              {isChCrawling && (
                <ProgressBar label={csvStrings.channelCrawlTab} current={channelCrawlProgress.crawledMembers} total={channelCrawlProgress.totalMembers} />
              )}
              {isFrCrawling && (
                <ProgressBar label={csvStrings.crawlTab} current={friendCrawlProgress.crawledMembers} total={friendCrawlProgress.totalMembers} />
              )}
            </div>
          )}

          {/* Crawl results */}
          {hasCrawlResults && (
            <div className="grid gap-2 rounded-2xl border border-border/70 bg-surface/70 p-4">
              {isChDone && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink">
                    {csvStrings.channelCrawlTab}
                    <span className="ml-2 text-xs text-muted">{channelCrawlProgress.crawledMembers}{csvStrings.migrateErrorsSuffix}</span>
                  </span>
                  <button type="button" className="text-xs font-semibold text-accent hover:text-accent-mid transition" onClick={handleDownloadChannelCsv}>
                    {csvStrings.downloadCsv}
                  </button>
                </div>
              )}
              {isChDone && isFrDone && <div className="border-t border-border/50" />}
              {isFrDone && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink">
                    {csvStrings.crawlTab}
                    <span className="ml-2 text-xs text-muted">{friendCrawlProgress.crawledMembers}{csvStrings.migrateErrorsSuffix}</span>
                  </span>
                  <button type="button" className="text-xs font-semibold text-accent hover:text-accent-mid transition" onClick={handleDownloadFriendCsv}>
                    {csvStrings.downloadCsv}
                  </button>
                </div>
              )}
              {channelDownloadError && <p className="text-xs text-red-500">{channelDownloadError}</p>}
              {friendDownloadError && <p className="text-xs text-red-500">{friendDownloadError}</p>}
            </div>
          )}

          {/* Crawl errors */}
          {chPhase === 'error' && (
            <p className="rounded-2xl border border-red-200 bg-red-50/50 px-4 py-3 text-sm text-red-600">{channelCrawlProgress.error}</p>
          )}
          {frPhase === 'error' && (
            <p className="rounded-2xl border border-red-200 bg-red-50/50 px-4 py-3 text-sm text-red-600">{friendCrawlProgress.error}</p>
          )}
        </div>

        {/* Section 2: CSV Upload */}
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <SectionLabel>CSVアップロード</SectionLabel>
            <div className="flex rounded-full border border-border/70 p-0.5" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={csvMode === 'channel'}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${csvMode === 'channel' ? 'bg-accent text-white shadow-sm' : 'text-muted hover:text-ink'}`}
                onClick={() => setCsvMode('channel')}
              >
                {csvStrings.channelRegisterTab}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={csvMode === 'friend'}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${csvMode === 'friend' ? 'bg-accent text-white shadow-sm' : 'text-muted hover:text-ink'}`}
                onClick={() => setCsvMode('friend')}
              >
                {csvStrings.migrateTab}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 bg-surface/70 p-4">
            {/* Channel CSV upload */}
            {csvMode === 'channel' && (
              <div className="grid gap-3">
                {registerPhase === 'idle' && !channelConfirmed && (
                  <>
                    <input ref={channelFileInputRef} type="file" accept=".csv" className="hidden" aria-label={csvStrings.channelUploadCsv} onChange={handleChannelFileChange} />
                    <button
                      type="button"
                      className="w-full rounded-2xl border border-dashed border-accent/30 bg-accent-soft/30 px-4 py-3 text-xs font-semibold text-accent transition hover:border-accent/50 hover:bg-accent-soft/50"
                      onClick={() => channelFileInputRef.current?.click()}
                    >
                      {csvStrings.channelUploadCsv}
                    </button>

                    {channelCsvEntries.length > 0 && (
                      <>
                        <div className="rounded-xl bg-white/60 p-3">
                          <p className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-muted/70">{csvStrings.previewTitle} ({channelCsvEntries.length}{csvStrings.migrateErrorsSuffix})</p>
                          <div className="grid gap-0.5">
                            {channelCsvEntries.slice(0, 5).map((entry, i) => (
                              <p key={i} className="text-xs text-ink">@{entry.basic_id} / {entry.channel_id}</p>
                            ))}
                            {channelCsvEntries.length > 5 && (
                              <p className="text-xs text-muted">...{csvStrings.previewMore} {channelCsvEntries.length - 5} {csvStrings.previewMoreSuffix}</p>
                            )}
                          </div>
                        </div>
                        <Button type="button" size="sm" variant="primary" fullWidth onClick={handleConfirmRegister}>
                          {csvStrings.channelStartRegister}
                        </Button>
                      </>
                    )}
                  </>
                )}

                {registerPhase === 'registering' && (
                  <ProgressBar label={csvStrings.channelRegisterTab} current={registerProgress.processedEntries} total={registerProgress.totalEntries} />
                )}

                {registerPhase === 'completed' && (
                  <div className="grid gap-2">
                    <div className="flex gap-3 text-sm">
                      <span className="text-ink">{csvStrings.channelRegisterResult} <strong>{registerProgress.registered}</strong></span>
                      <span className="text-muted">{csvStrings.channelRegisterResultSkipped} {registerProgress.skipped}</span>
                      <span className="text-red-500">{csvStrings.channelRegisterResultFailed} {registerProgress.failed}</span>
                    </div>
                    {registerProgress.registered > 0 && (
                      <div className="rounded-xl border border-accent/20 bg-accent-soft/20 px-4 py-3 text-xs text-accent">
                        {csvStrings.channelRegisterDoneHint}
                      </div>
                    )}
                    {registerProgress.errors.length > 0 && (
                      <div className="max-h-[25vh] overflow-y-auto rounded-xl bg-red-50/50 p-3">
                        {registerProgress.errors.map((err, i) => (
                          <p key={i} className="text-xs text-red-600">@{err.basic_id} ({err.channel_id}): {err.reason}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {registerPhase === 'error' && <p className="text-sm text-red-500">{registerProgress.error}</p>}
              </div>
            )}

            {/* Friend CSV upload */}
            {csvMode === 'friend' && (
              <div className="grid gap-3">
                {migratePhase === 'idle' && !confirmed && (
                  <>
                    <input ref={fileInputRef} type="file" accept=".csv" className="hidden" aria-label={csvStrings.uploadCsv} onChange={handleFriendFileChange} />
                    <button
                      type="button"
                      className="w-full rounded-2xl border border-dashed border-accent/30 bg-accent-soft/30 px-4 py-3 text-xs font-semibold text-accent transition hover:border-accent/50 hover:bg-accent-soft/50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {csvStrings.uploadCsv}
                    </button>

                    {csvEntries.length > 0 && (
                      <>
                        <div className="rounded-xl bg-white/60 p-3">
                          <p className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-muted/70">{csvStrings.previewTitle} ({csvEntries.length}{csvStrings.migrateErrorsSuffix})</p>
                          <div className="grid gap-0.5">
                            {csvEntries.slice(0, 5).map((entry, i) => (
                              <p key={i} className="text-xs text-ink">{entry.line_user_id} / {entry.basic_id}</p>
                            ))}
                            {csvEntries.length > 5 && (
                              <p className="text-xs text-muted">...{csvStrings.previewMore} {csvEntries.length - 5} {csvStrings.previewMoreSuffix}</p>
                            )}
                          </div>
                        </div>
                        <Button type="button" size="sm" variant="primary" fullWidth onClick={handleConfirmMigrate}>
                          {csvStrings.startMigrate}
                        </Button>
                      </>
                    )}
                  </>
                )}

                {migratePhase === 'migrating' && (
                  <ProgressBar label={csvStrings.migrateTab} current={migrateProgress.processedEntries} total={migrateProgress.totalEntries} />
                )}

                {migratePhase === 'completed' && (
                  <div className="grid gap-2">
                    <div className="flex gap-3 text-sm">
                      <span className="text-ink">{csvStrings.migrateResult} <strong>{migrateProgress.migratedMembers}</strong></span>
                      <span className="text-muted">{csvStrings.migrateResultSkipped} {migrateProgress.skippedMembers}</span>
                      <span className="text-red-500">{csvStrings.migrateResultFailed} {migrateProgress.failedMembers}</span>
                    </div>

                    {migrateProgress.errors.length > 0 && (
                      <div className="max-h-[25vh] overflow-y-auto rounded-xl bg-red-50/50 p-3">
                        {migrateProgress.errors.map((err, i) => (
                          <p key={i} className="text-xs text-red-600">{err.basic_id} ({err.line_user_id}): {err.reason}</p>
                        ))}
                      </div>
                    )}

                    {isMessageRunning && messageProgress && (
                      <ProgressBar label={msgStrings.sending} current={messageProgress.current} total={messageProgress.total} />
                    )}

                    {!isMessageRunning && messageProgress && (
                      <div className="grid gap-0.5 rounded-xl bg-white/60 p-3">
                        {messageProgress.results.map((r, i) => {
                          const account = accounts.find((a) => a.id === r.lineChannelId);
                          const label = account ? `@${account.basicId}` : `#${r.lineChannelId}`;
                          if (r.status === 'success' && r.data) {
                            return (
                              <p key={i} className="text-xs text-green-700">
                                {label}: {csvStrings.messageResult} {r.data.sent}{csvStrings.migrateErrorsSuffix} / {csvStrings.messageResultFailed} {r.data.failed}{csvStrings.migrateErrorsSuffix}
                              </p>
                            );
                          }
                          return <p key={i} className="text-xs text-red-500">{label}: {r.error}</p>;
                        })}
                      </div>
                    )}

                    {!isMessageRunning && (migrateProgress.migratedMembers > 0 || migrateProgress.skippedMembers > 0) && (
                      <Button type="button" size="sm" variant="primary" fullWidth disabled={isMessageRunning} onClick={handleSendMessages}>
                        {msgStrings.sendAll}
                      </Button>
                    )}
                  </div>
                )}

                {migratePhase === 'error' && <p className="text-sm text-red-500">{migrateProgress.error}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end">
          <button type="button" className="text-xs font-medium text-muted hover:text-ink transition" onClick={handleClose}>
            {commonStrings.close}
          </button>
        </div>
      </div>
    </Modal>
  );
}
